// AGC: Drill-down sheet for the "Necessário/Tarefas Pendentes" total shown on a
// AGC: tank/silo card. Lists each pending task that contributes to the total,
// AGC: with an editable L/ha (or kg/ha) field per row. Editing here writes the
// AGC: quantityPerHa back onto the task (via PUT /api/v1/tasks/:id) and
// AGC: recomputes totalQuantity — the tank card's red number follows.

import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { X, Loader2, AlertTriangle } from 'lucide-react'
import { clampDecimals } from '../../utils'
import type { Task, TaskInputEntry } from '../../types/task'
import { TASK_CATEGORIES } from '../../types/task'
import { updateTask } from '../../services/tasks'
import { useAuth } from '../../hooks/useAuth'

export interface NecessarioTaskRow {
  taskId: string
  title: string
  category: string
  plotName: string | null
  plotArea: number
  inputIndex: number                 // AGC: position of the diesel (or whichever) input in task.inputs[]
  quantityPerHa: number
  totalQuantity: number
  unit: string
  status: string
  // AGC: Snapshot of the full task so we can PUT the whole inputs[] on save.
  task: Task
}

interface Props {
  isOpen: boolean
  onClose: () => void
  productName: string                 // AGC: e.g. "Diesel" — for the sheet title
  unit: string                        // AGC: e.g. "L" — for totals display
  rows: NecessarioTaskRow[]
  onRowSaved?: (updatedTask: Task) => void   // AGC: let caller refresh local state / query
}

function categoryInfo(id: string) {
  return TASK_CATEGORIES.find(c => c.id === (id || '').toLowerCase()) || { icon: '📋', name: id || '—' }
}

export const NecessarioBreakdownSheet: React.FC<Props> = ({ isOpen, onClose, productName, unit, rows, onRowSaved }) => {
  const { t } = useTranslation()
  const { getAuthToken } = useAuth()
  // AGC: Local draft values keyed by taskId. Persist only on save-click.
  const [drafts, setDrafts] = useState<Record<string, number>>({})
  const [savingId, setSavingId] = useState<string | null>(null)
  const [errorByTask, setErrorByTask] = useState<Record<string, string>>({})

  useEffect(() => {
    if (!isOpen) {
      setDrafts({})
      setSavingId(null)
      setErrorByTask({})
    }
  }, [isOpen])

  if (!isOpen) return null

  const effectiveQty = (row: NecessarioTaskRow): number => {
    const d = drafts[row.taskId]
    return typeof d === 'number' ? d : row.quantityPerHa
  }

  const effectiveTotal = (row: NecessarioTaskRow): number => effectiveQty(row) * (row.plotArea || 0)

  const total = rows.reduce((sum, r) => sum + effectiveTotal(r), 0)
  const hasDirty = Object.keys(drafts).length > 0

  const handleSave = async (row: NecessarioTaskRow) => {
    const newPerHa = drafts[row.taskId]
    if (newPerHa == null || !(newPerHa >= 0)) return
    try {
      setSavingId(row.taskId)
      setErrorByTask(prev => ({ ...prev, [row.taskId]: '' }))
      // AGC: Rebuild the full inputs array with the updated entry. Mutating a
      // AGC: single index keeps the rest of the inputs intact (sementes, map/dap, etc.).
      const updatedInputs: TaskInputEntry[] = (row.task.inputs || []).map((inp, idx) => {
        if (idx !== row.inputIndex) return inp
        const next = { ...(inp as TaskInputEntry) }
        next.quantityPerHa = newPerHa
        next.totalQuantity = newPerHa * (row.plotArea || 0)
        return next as TaskInputEntry
      })
      const token = await getAuthToken()
      const updated = await updateTask(row.taskId, { inputs: updatedInputs }, token)
      if (onRowSaved) onRowSaved(updated)
      // AGC: Clear the draft for this row — the parent will re-pass fresh rows next render.
      setDrafts(prev => {
        const next = { ...prev }
        delete next[row.taskId]
        return next
      })
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'error'
      setErrorByTask(prev => ({ ...prev, [row.taskId]: msg }))
    } finally {
      setSavingId(null)
    }
  }

  return (
    <div className="fixed inset-0 z-[95] flex items-end sm:items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="bg-white dark:bg-slate-900 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-lg max-h-[85vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* AGC: Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-slate-700">
          <div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">
              {t('inventory.breakdown.title', { product: productName })}
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {t('inventory.breakdown.subtitle', { count: rows.length })}
            </p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-lg">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* AGC: Rows */}
        <div className="flex-1 overflow-y-auto">
          {rows.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400 italic">
              {t('inventory.breakdown.empty')}
            </div>
          ) : (
            <ul className="divide-y divide-gray-100 dark:divide-slate-700">
              {rows.map(row => {
                const cat = categoryInfo(row.category)
                const draft = drafts[row.taskId]
                const isDirty = draft != null
                const isSaving = savingId === row.taskId
                const err = errorByTask[row.taskId]
                const totalForRow = effectiveTotal(row)
                return (
                  <li key={row.taskId} className="px-4 py-3">
                    <div className="flex items-start gap-2 mb-2">
                      <span className="text-lg leading-none mt-0.5">{cat.icon}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{row.title}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {cat.name} · {row.plotName || '—'} · {row.plotArea.toLocaleString('pt-BR', { maximumFractionDigits: 2 })} ha
                        </p>
                      </div>
                      <span className="text-sm font-semibold text-red-600 tabular-nums">
                        {totalForRow.toLocaleString('pt-BR', { maximumFractionDigits: 1 })} {row.unit || unit}
                      </span>
                    </div>

                    <div className="flex items-center gap-2">
                      <label className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                        {row.unit || unit}/ha:
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        className={`w-24 px-2 py-1 text-sm text-right border rounded focus:outline-none focus:ring-2 focus:ring-emerald-400 ${isDirty ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20' : 'border-gray-300 dark:border-slate-600'}`}
                        value={draft != null ? draft : row.quantityPerHa}
                        onChange={e => {
                          const v = e.target.value === '' ? 0 : parseFloat(e.target.value)
                          setDrafts(prev => ({ ...prev, [row.taskId]: isNaN(v) ? 0 : clampDecimals(v) }))
                        }}
                        disabled={isSaving}
                      />
                      <span className="text-xs text-gray-400">
                        × {row.plotArea.toLocaleString('pt-BR', { maximumFractionDigits: 2 })} ha
                      </span>
                      {isDirty && (
                        <button
                          onClick={() => handleSave(row)}
                          disabled={isSaving}
                          className="ml-auto px-3 py-1 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium rounded disabled:opacity-50 flex items-center gap-1"
                        >
                          {isSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                          {t('common.save')}
                        </button>
                      )}
                    </div>

                    {err && (
                      <div className="mt-2 flex items-start gap-1.5 text-xs text-red-600 dark:text-red-400">
                        <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                        <span>{err}</span>
                      </div>
                    )}
                  </li>
                )
              })}
            </ul>
          )}
        </div>

        {/* AGC: Total footer */}
        <div className="px-4 py-3 border-t border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800 flex items-center justify-between">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {t('inventory.breakdown.total')}
          </span>
          <span className={`text-base font-bold ${hasDirty ? 'text-emerald-600' : 'text-red-600'} tabular-nums`}>
            {total.toLocaleString('pt-BR', { maximumFractionDigits: 1 })} {unit}
            {hasDirty && <span className="ml-1 text-xs text-gray-400 font-normal">(previsto)</span>}
          </span>
        </div>
      </div>
    </div>
  )
}

export default NecessarioBreakdownSheet
